import {
	DeviceUserPerspective,
	LocalizationSensitivity,
	ReflectionCorrection,
	VoiceActivityDetectionSensitivity
} from '@shure/shared/angular/data-access/system-api/core';
import {
	AnalogOutputMode,
	CoverageMode,
	DeviceAuthentication,
	DeviceModel,
	DeviceStatus,
	MeasurementUnit,
	NumberRange,
	Placement
} from '@shure/shared/angular/data-access/system-api/models';
import { PositionPoint, PositionUnit } from '@shure/shared/angular/utils/utils-position';

import { CoverageAudioChannel } from './coverage-audio-channel';
import { CoverageAnalogOutputChannel } from './coverage-audio-channel-passive-speaker';
import { CoverageAudioCoverageArea } from './coverage-audio-coverage-area';

export type FeatureValue<T> = { value: T; isMissing: boolean };
export type FeatureSet<T> = { [P in keyof T]?: FeatureValue<T[P]> };

export const DEVICE_DEFAULTS: Pick<CoverageDeviceFeatures, 'placement'> = {
	placement: Placement.WallHorizontal
};

export type CoverageDeviceRotation = {
	/** Rotation in the x plane of the device*/
	xAxis: number;
	/** Rotation in the y plane of the device. Only populated by devices that expose it. */
	yAxis?: number;
	/** Rotation on the z plane of the device */
	zAxis: number;
};

export type TalkerTrackingConfig = {
	localizationSensitivity?: LocalizationSensitivity;
	metricsIncluded?: boolean;
	positionUpdateRate?: number | null;
	reflectionCorrection?: ReflectionCorrection;
	voiceActivityDetectionSensitivity?: VoiceActivityDetectionSensitivity;
};

export type CoverageDeviceFeatures = {
	audioChannels: CoverageAudioChannel[];
	audioCoverageAreas: CoverageAudioCoverageArea[];
	analogOutputChannels: CoverageAnalogOutputChannel[];
	analogAudioOutputMode: AnalogOutputMode;
	authentication: DeviceAuthentication;
	coverageMode: CoverageMode;
	position: PositionPoint;
	rotation: CoverageDeviceRotation;
	placement: Placement;
	detectedPlacement?: Placement;
	height: PositionUnit;
	listenerHeight: PositionUnit;
	audioAutoFocus: { enabled: boolean };
	debugMode: { enabled: boolean };
	dspDebugMode: { enabled: boolean };
	identify: { identifying: boolean };
	talkerTrackingConfig?: TalkerTrackingConfig;
	name: string;
	coverageLock: { enabled: boolean };
	automixGainMetering: { enabled: boolean };
	mute: { muted: boolean };
	danteName: string;
	userPerspective: DeviceUserPerspective;
	measurementUnit: MeasurementUnit;
	allEqualizersBypass: boolean;
};

export type CoverageDevice = {
	id: string;
	model: DeviceModel;
	status: DeviceStatus;
	virtual: boolean;
	automixmodeManualChannelId?: string;
	features: FeatureSet<CoverageDeviceFeatures>;
	constraints?: {
		talkerTrackingConfig: {
			positionUpdateRate: NumberRange;
		};
		height?: {
			height: NumberRange;
		};
		name?: string;
	};
};

export type CoverageTalkerPosition = {
	confidenceLevel: number;
	x: PositionUnit;
	y: PositionUnit;
	z: PositionUnit;
};
